import styled from '@emotion/styled';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
// eslint-disable-next-line no-restricted-imports
import type {DefaultValues, FieldValues} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {
  EAutocompleteOption,
  ECancelSubmitButtonRow,
  EForm,
} from 'webapp-lib/Components';
import {mixinFlexboxColumn, mixinFlexboxRow} from 'webapp-lib/styling';
import {useDebouncedValue} from 'webapp-lib/utils';

import {Dialog} from '#';
import {useDialogController} from '_/hooks';
import {useTablePersistence} from '_/hooks/useTablePersistence';
import {
  Control,
  EFAutocomplete,
  EFAutocompleteProps,
  useForm,
  useWatch,
} from '_/utilities/form';
import {
  HandledArgs,
  HandledResponseType,
  handlePaginatedLoadableFetch,
  usePaginatedItemLoader,
} from '_/utilities/simple-fetch';
import {
  defaultPaginationArguments,
  PageItem,
  PaginatedLoadable,
  PaginationArguments,
  PaginationDirection,
  PaginationMode,
  PaginationResponse,
  State,
} from '_/utilities/types';

import {EFTagsAutocomplete} from '../ETagsAutocomplete';
import {OptionFilterInfo} from '../OptionFilterInfo/OptionFilterInfo';

const NO_ADDITIONAL_ITEMS: never[] = [];

const CREATE_OPTION_VALUE = 'autocompleteBase.createOption';

export type AutocompleteLoadContext<TFilterState> = {
  searchQuery: string;
  filterState: TFilterState | undefined;
};

/**
 * Loads the items of an autocomplete. Extra query parameters
 * (excludeCurrentUser, casusId, ...) live in the closure of the loader - the
 * base never sees them.
 */
export type AutocompleteLoader<TItem, TFilterState = never> = (
  paginationArguments: Omit<PaginationArguments, 'cursor'>,
  currentLoadable: PaginatedLoadable<PageItem<TItem>[]>,
  context: AutocompleteLoadContext<TFilterState>
) => Promise<PaginatedLoadable<PageItem<TItem>[]>>;

/**
 * Called when the user selects or clears an option. `context.value` is the raw
 * change value (also set for options without a backing item, e.g.
 * placeholders) and `context.searchQuery` the live search input.
 */
export type AutocompleteChangeHandler<TItem> = (
  item: TItem | undefined,
  context: {value: unknown; searchQuery: string}
) => void;

/**
 * Pinned as the first option and always displayed. Selecting it never changes
 * the form value; `onSelect` is called instead (e.g. to open a creation
 * dialog). Calling `commit` selects an item afterwards (e.g. the created one);
 * if a committed item is missing from the next successfully loaded options
 * (e.g. filtered out), the selection is cleared again.
 *
 * Only supported on single-select autocompletes without client-side option
 * filtering (`noOptionFiltering` must be set). With `categories` a
 * `categoryName` is required - uncategorized options are not rendered - and
 * the option is pinned first within that category.
 */
export type AutocompleteCreateOption = {
  displayText: string;
  categoryName?: string;
  onSelect: (onCreate: (itemId: string) => void) => void;
  /**
   * Reset the filter to its default state when an item has been created, so a
   * freshly created item is not hidden by a stale filter (and thus not cleared
   * by the missing-item safety net).
   */
  resetFilterOnCreate?: boolean;
};

/**
 * Configures the filter dialog of an autocomplete. Its presence enables the
 * filter button, the dialog, the filter summary below the input and (if a
 * `persistenceKey` is set) the persistence of the filter state.
 */
export type AutocompleteFilter<TFilterState extends FieldValues> = {
  defaultState: TFilterState;
  /** Renders the form fields of the filter dialog. */
  renderFields: (control: Control<TFilterState>) => React.ReactElement;
  /** Short summary of the filter state, shown below the input. */
  toText: (state: TFilterState) => string;
  dialogHeader: string;
  /** The filter state is persisted only when set. */
  persistenceKey?: string;
  /**
   * Validates the values of a restored persisted state beyond the generic
   * shape check, which cannot tell a stale enum value from a current one.
   * Provide this when the state contains enum-like values whose set may
   * change between releases. Invalid states fall back to `defaultState`.
   */
  isValidPersistedState?: (state: TFilterState) => boolean;
  /** Custom warning tooltip when the selected option is filtered out. */
  warningText?: string;
};

export type AutocompleteBaseProps<
  TItem,
  TFilterState extends FieldValues = never,
> = Omit<
  EFAutocompleteProps,
  | 'options'
  | 'customOnChange'
  | 'hasMoreBefore'
  | 'hasMoreAfter'
  | 'inputRef'
  | 'onFilterClick'
  | 'onLoadMoreBefore'
  | 'onLoadMoreAfter'
  | 'onSearchQueryChange'
  | 'optionFilterInfo'
> & {
  loadItems: AutocompleteLoader<TItem, TFilterState>;
  /**
   * Maps an item to its option. `value` must be the item id - it drives the
   * change lookups.
   */
  toOption: (item: TItem) => EAutocompleteOption;
  /**
   * Full control over the rendered option list (e.g. merging additional items
   * or assigning categories). Callers map items with their own `toOption`.
   */
  mapOptions?: (
    items: TItem[],
    context: {
      searchQuery: string;
      hasMoreBefore: boolean;
      filterState: TFilterState | undefined;
    }
  ) => EAutocompleteOption[];
  /**
   * Items that are always part of the change lookups even if they are not part
   * of the loaded items (e.g. recommended options).
   */
  additionalItems?: TItem[];
  /** Called when the user selects or clears an option. */
  onItemChange?: AutocompleteChangeHandler<TItem>;
  /**
   * Called whenever the selected form value changes, also on programmatic
   * changes and once the initially selected item is loaded.
   */
  onSelectedItemChange?: (item: TItem | undefined) => void;
  /**
   * Called when the selected item is not among the options after the initial
   * load, a filter change or a selection change. With `filter` set, a warning
   * badge is shown and the input is focused as well.
   */
  onSelectedItemMissing?: () => void;
  createOption?: AutocompleteCreateOption;
  filter?: AutocompleteFilter<TFilterState>;
  /**
   * Changing this value forces a refetch (e.g. on masterdata hydration or when
   * query parameters captured in the `loadItems` closure change).
   */
  reloadTrigger?: unknown;
};

export function AutocompleteBase<
  TItem,
  TFilterState extends FieldValues = never,
>({
  loadItems,
  toOption,
  mapOptions,
  additionalItems = NO_ADDITIONAL_ITEMS,
  onItemChange,
  onSelectedItemChange,
  onSelectedItemMissing,
  createOption,
  filter,
  reloadTrigger,
  multiple = false,
  ...autocompleteProps
}: AutocompleteBaseProps<TItem, TFilterState>): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  const selectedId = useSelectedId(autocompleteProps);
  const {filterState, resetFilterState, ready, onFilterClick, filterDialog} =
    useFilterState(filter);
  const data = useAutocompleteData(loadItems, {
    selectedId,
    multiple,
    ready,
    filterState,
    reloadTrigger,
  });
  const {createOptionEntry, interceptCreateSelection} = useCreateOption({
    createOption: multiple ? undefined : createOption,
    selectedId,
    toOption,
    items: data.items,
    loadState: data.loadState,
    resetSearchQuery: data.resetSearchQuery,
    resetFilterState,
    inputRef,
  });
  const options = useOptions({
    items: data.items,
    toOption,
    mapOptions,
    createOptionEntry,
    searchQuery: data.searchQuery,
    hasMoreBefore: data.hasMoreBefore,
    filterState,
  });
  const {showWarning} = useFilteredOutWarning({
    onSelectedItemMissing,
    warnInUi: filter !== undefined,
    selectedId,
    filterState,
    options,
    items: data.items,
    toOption,
    loadState: data.loadState,
    inputRef,
  });
  const handleChange = useChangeHandler({
    onItemChange,
    interceptCreateSelection,
    toOption,
    items: data.items,
    additionalItems,
    searchQuery: data.searchQuery,
  });
  useSelectedItemChange({
    onSelectedItemChange,
    selectedId,
    multiple,
    toOption,
    items: data.items,
    additionalItems,
  });

  const sharedProps = {
    ...autocompleteProps,
    customOnChange: handleChange,
    hasMoreAfter: data.hasMoreAfter,
    hasMoreBefore: data.hasMoreBefore,
    inputRef,
    optionFilterInfo: renderOptionFilterInfo(filter, filterState, showWarning),
    options,
    onFilterClick,
    onLoadMoreAfter: data.onLoadMoreAfter,
    onLoadMoreBefore: data.onLoadMoreBefore,
    onSearchQueryChange: data.onSearchQueryChange,
  };

  return (
    <>
      {multiple ? (
        <EFTagsAutocomplete {...sharedProps} />
      ) : (
        <EFAutocomplete {...sharedProps} />
      )}
      {filterDialog}
    </>
  );
}

/**
 * Restored persisted filter states are raw JSON from localStorage and may have
 * an outdated schema - they are discarded unless their shape matches the
 * default state.
 */
function matchesDefaultStateShape<TFilterState extends FieldValues>(
  value: unknown,
  defaultState: TFilterState
): value is TFilterState {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return Object.entries(defaultState).every(([key, defaultValue]) =>
    Array.isArray(defaultValue)
      ? Array.isArray(record[key])
      : typeof record[key] === typeof defaultValue
  );
}

function useFilterState<TFilterState extends FieldValues>(
  filter: AutocompleteFilter<TFilterState> | undefined
): {
  filterState: TFilterState | undefined;
  resetFilterState: () => void;
  ready: boolean;
  onFilterClick: (() => void) | undefined;
  filterDialog: React.ReactElement | null;
} {
  const {t} = useTranslation('common');
  const {isVisible, showDialog, closeDialog} = useDialogController();
  const [filterState, setFilterState] = useState(filter?.defaultState);

  const ready = useTablePersistence({
    key: filter?.persistenceKey ?? 'autocompleteBase.unused',
    enabled: filter?.persistenceKey !== undefined,
    filters: filterState,
    filtersUpdated: (restored: unknown) => {
      if (
        filter &&
        matchesDefaultStateShape(restored, filter.defaultState) &&
        (filter.isValidPersistedState?.(restored) ?? true)
      ) {
        setFilterState(restored);
      }
    },
    plainFilterObject: true,
  });

  const {control, handleSubmit, formState, reset} = useForm<TFilterState>({
    // the unresolved generic cannot be proven to match DefaultValues
    defaultValues: filter?.defaultState as DefaultValues<TFilterState>,
    onSubmit: (values) => {
      setFilterState(values);
      closeDialog();
    },
  });

  // Sync the form to the current (possibly persisted) state on every open.
  useEffect(() => {
    if (isVisible) {
      reset(filterState);
    }
    // the form is only reset when the dialog opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const filterDialog =
    filter && isVisible ? (
      <Dialog
        headerText={filter.dialogHeader}
        size='tiny'
        isShown
        onClose={closeDialog}
      >
        <EForm onSubmit={handleSubmit}>
          {filter.renderFields(control)}
          <ECancelSubmitButtonRow
            isSubmitting={formState.isSubmitting}
            submitButtonText={t('actions.apply')}
            onCancel={closeDialog}
          />
        </EForm>
      </Dialog>
    ) : null;

  return {
    filterState,
    resetFilterState: () => setFilterState(filter?.defaultState),
    ready,
    onFilterClick: filter ? showDialog : undefined,
    filterDialog,
  };
}

/**
 * Calls `onSelectedItemMissing` when the selected item is not among the
 * options after the initial load, a filter change or a selection change -
 * never on pagination or while typing. With `warnInUi` it also shows the
 * warning badge and focuses the input.
 */
function useFilteredOutWarning<TItem, TFilterState>({
  onSelectedItemMissing,
  warnInUi,
  selectedId,
  filterState,
  options,
  items,
  toOption,
  loadState,
  inputRef,
}: {
  onSelectedItemMissing: (() => void) | undefined;
  warnInUi: boolean;
  selectedId: string | undefined;
  filterState: TFilterState | undefined;
  options: EAutocompleteOption[];
  items: TItem[];
  toOption: (item: TItem) => EAutocompleteOption;
  loadState: State;
  inputRef: React.RefObject<HTMLInputElement | null>;
}): {showWarning: boolean} {
  const [showWarning, setShowWarning] = useState(false);
  const lastChecked = useRef<{
    selectedId: string | undefined;
    filterState: TFilterState | undefined;
  }>(undefined);

  // `items` changes identity only when a fetch settles, so the check runs
  // against the result of the fetch that a selection or filter change
  // triggered - never against a stale option list. Load errors are skipped to
  // not report a present item as missing.
  useEffect(() => {
    const alreadyChecked =
      lastChecked.current !== undefined &&
      lastChecked.current.selectedId === selectedId &&
      lastChecked.current.filterState === filterState;
    if (
      !onSelectedItemMissing ||
      loadState !== State.Success ||
      alreadyChecked
    ) {
      return;
    }
    lastChecked.current = {selectedId, filterState};
    if (!selectedId) {
      return;
    }
    // Displayable, not merely resolvable: `mapOptions` may drop a loaded entry.
    const isPresent =
      options.some((option) => option.value === selectedId) ||
      items.some((item) => toOption(item).value === selectedId);
    if (!isPresent) {
      onSelectedItemMissing();
      if (warnInUi) {
        inputRef.current?.focus();
      }
    }
    setShowWarning(warnInUi && !isPresent);
    // selectedId/filterState/options are read from the settling render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loadState]);

  return {showWarning};
}

function renderOptionFilterInfo<TFilterState extends FieldValues>(
  filter: AutocompleteFilter<TFilterState> | undefined,
  filterState: TFilterState | undefined,
  showWarning: boolean
): React.ReactElement | undefined {
  return filter ? (
    <OptionFilterInfo
      filteredOutWarningText={filter.warningText}
      optionFilterText={filter.toText(filterState ?? filter.defaultState)}
      showFilteredOutWarning={showWarning}
    />
  ) : undefined;
}

function useSelectedId(
  props: Pick<EFAutocompleteProps, 'control' | 'name' | 'defaultValue'>
): string | undefined {
  const value = useWatch({
    control: props.control,
    name: props.name,
    defaultValue: props.defaultValue,
  });
  return typeof value === 'string' && value !== '' ? value : undefined;
}

type AutocompleteData<TItem> = {
  items: TItem[];
  hasMoreBefore: boolean;
  hasMoreAfter: boolean;
  loadState: State;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  resetSearchQuery: () => void;
  onLoadMoreBefore: () => Promise<void>;
  onLoadMoreAfter: () => Promise<void>;
};

/**
 * Owns the item loader, the debounced search query and the
 * navigate-to-selected-item effect.
 */
function useAutocompleteData<TItem, TFilterState>(
  loadItems: AutocompleteLoader<TItem, TFilterState>,
  {
    selectedId,
    multiple,
    ready,
    filterState,
    reloadTrigger,
  }: {
    selectedId: string | undefined;
    multiple: boolean;
    ready: boolean;
    filterState: TFilterState | undefined;
    reloadTrigger: unknown;
  }
): AutocompleteData<TItem> {
  const [
    debouncedSearchQuery,
    searchQuery,
    setSearchQuery,
    setSearchQueryUnbounced,
  ] = useDebouncedValue('');
  const {loadable, fetch, fetchMore} = usePaginatedItemLoader<
    PageItem<TItem>[],
    AutocompleteLoadContext<TFilterState>
  >(loadItems);

  useEffect(() => {
    if (!ready) {
      return;
    }
    if (selectedId && !debouncedSearchQuery && !multiple) {
      fetch(
        {mode: PaginationMode.Navigate, key: [selectedId]},
        {searchQuery: '', filterState}
      );
    } else {
      fetch(defaultPaginationArguments, {
        searchQuery: debouncedSearchQuery,
        filterState,
      });
    }
    // reloadTrigger intentionally retriggers the fetch without being read
  }, [
    debouncedSearchQuery,
    filterState,
    selectedId,
    ready,
    multiple,
    reloadTrigger,
    fetch,
  ]);

  const items = useMemo(
    () => (loadable.data ?? []).map((pageItem) => pageItem.item),
    [loadable.data]
  );

  const onLoadMoreBefore = useCallback(
    () =>
      fetchMore(
        {mode: PaginationMode.Browse, direction: PaginationDirection.Backward},
        {searchQuery: debouncedSearchQuery, filterState}
      ),
    [fetchMore, debouncedSearchQuery, filterState]
  );

  const onLoadMoreAfter = useCallback(
    () =>
      fetchMore(
        {mode: PaginationMode.Browse, direction: PaginationDirection.Forward},
        {searchQuery: debouncedSearchQuery, filterState}
      ),
    [fetchMore, debouncedSearchQuery, filterState]
  );

  return {
    items,
    hasMoreBefore: loadable.hasMoreBefore,
    hasMoreAfter: loadable.hasMoreAfter,
    loadState: loadable.state,
    searchQuery,
    onSearchQueryChange: setSearchQuery,
    // resets the live and the debounced query synchronously
    resetSearchQuery: () => setSearchQueryUnbounced(''),
    onLoadMoreBefore,
    onLoadMoreAfter,
  };
}

/**
 * Owns the pinned create option: builds its entry, intercepts its selection
 * (the form value never becomes the sentinel) and clears the selection again
 * when a committed item is missing from the next successfully loaded items
 * (e.g. filtered out). Load errors never clear the selection.
 */
function useCreateOption<TItem>({
  createOption,
  selectedId,
  toOption,
  items,
  loadState,
  resetSearchQuery,
  resetFilterState,
  inputRef,
}: {
  createOption: AutocompleteCreateOption | undefined;
  selectedId: string | undefined;
  toOption: (item: TItem) => EAutocompleteOption;
  items: TItem[];
  loadState: State;
  resetSearchQuery: () => void;
  resetFilterState: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}): {
  createOptionEntry: EAutocompleteOption | undefined;
  interceptCreateSelection: (
    event: React.ChangeEvent<any>,
    originalEventHandler: React.ChangeEventHandler
  ) => boolean;
} {
  const pendingCommit = useRef<{
    itemId: string;
    clearSelection: () => void;
  }>(undefined);

  const createOptionEntry = useMemo(
    (): EAutocompleteOption | undefined =>
      createOption && {
        value: CREATE_OPTION_VALUE,
        displayText: createOption.displayText,
        categoryName: createOption.categoryName,
        isPlaceholderOption: true,
      },
    [createOption]
  );

  const interceptCreateSelection = (
    event: React.ChangeEvent<any>,
    originalEventHandler: React.ChangeEventHandler
  ): boolean => {
    if (!createOption || event.target.value !== CREATE_OPTION_VALUE) {
      return false;
    }
    // ensure the next fetch after a commit takes the navigate-to-item branch
    resetSearchQuery();
    // resets the transient create option text shown in the input
    inputRef.current?.blur();
    const buildFieldEvent = (value: string) =>
      ({target: {name: event.target.name, value}}) as React.ChangeEvent<any>;
    createOption.onSelect((itemId) => {
      pendingCommit.current = {
        itemId,
        clearSelection: () => originalEventHandler(buildFieldEvent('')),
      };
      // reset the filter first so the navigate-fetch the creation triggers uses
      // the default state and does not hide the freshly created item
      if (createOption.resetFilterOnCreate) {
        resetFilterState();
      }
      originalEventHandler(buildFieldEvent(itemId));
    });
    return true;
  };

  // `items` changes identity only when a fetch settles, so the committed item
  // is checked against the result of the fetch its commit triggered.
  useEffect(() => {
    const commit = pendingCommit.current;
    if (!commit || loadState === State.Loading) {
      return;
    }
    if (selectedId !== commit.itemId) {
      pendingCommit.current = undefined;
      return;
    }
    if (loadState === State.Error) {
      return;
    }
    pendingCommit.current = undefined;
    const isPresent = items.some(
      (item) => toOption(item).value === commit.itemId
    );
    if (!isPresent) {
      commit.clearSelection();
    }
    // selectedId/toOption are read from the settling render's closure
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, loadState]);

  return {createOptionEntry, interceptCreateSelection};
}

function useOptions<TItem, TFilterState extends FieldValues>({
  items,
  toOption,
  mapOptions,
  createOptionEntry,
  searchQuery,
  hasMoreBefore,
  filterState,
}: {
  items: TItem[];
  toOption: (item: TItem) => EAutocompleteOption;
  mapOptions: AutocompleteBaseProps<TItem, TFilterState>['mapOptions'];
  createOptionEntry: EAutocompleteOption | undefined;
  searchQuery: string;
  hasMoreBefore: boolean;
  filterState: TFilterState | undefined;
}): EAutocompleteOption[] {
  return useMemo(
    () => [
      ...(createOptionEntry ? [createOptionEntry] : []),
      ...(mapOptions
        ? mapOptions(items, {searchQuery, hasMoreBefore, filterState})
        : items.map(toOption)),
    ],
    [
      items,
      toOption,
      mapOptions,
      createOptionEntry,
      searchQuery,
      hasMoreBefore,
      filterState,
    ]
  );
}

function useChangeHandler<TItem>({
  onItemChange,
  interceptCreateSelection,
  toOption,
  items,
  additionalItems,
  searchQuery,
}: {
  onItemChange: AutocompleteChangeHandler<TItem> | undefined;
  interceptCreateSelection: (
    event: React.ChangeEvent<any>,
    originalEventHandler: React.ChangeEventHandler
  ) => boolean;
  toOption: (item: TItem) => EAutocompleteOption;
  items: TItem[];
  additionalItems: TItem[];
  searchQuery: string;
}): (
  event: React.ChangeEvent<any>,
  originalEventHandler: React.ChangeEventHandler
) => void {
  return useCallback(
    (event, originalEventHandler) => {
      if (interceptCreateSelection(event, originalEventHandler)) {
        return;
      }
      if (onItemChange) {
        const {value} = event.target;
        const selected = [...additionalItems, ...items].find(
          (item) => toOption(item).value === value
        );
        onItemChange(selected, {value, searchQuery});
      }
      originalEventHandler(event);
    },
    [
      onItemChange,
      interceptCreateSelection,
      toOption,
      items,
      additionalItems,
      searchQuery,
    ]
  );
}

/**
 * Calls `onSelectedItemChange` whenever the selected form value changes - also
 * on programmatic changes and once the initially selected item is loaded.
 *
 * Single-select only: `onSelectedItemChange` reports one item, which cannot
 * express a multi-select value.
 */
function useSelectedItemChange<TItem>({
  onSelectedItemChange,
  selectedId,
  multiple,
  toOption,
  items,
  additionalItems,
}: {
  onSelectedItemChange: ((item: TItem | undefined) => void) | undefined;
  selectedId: string | undefined;
  multiple: boolean;
  toOption: (item: TItem) => EAutocompleteOption;
  items: TItem[];
  additionalItems: TItem[];
}): void {
  // Both the id and whether it could be resolved to an item are needed: a
  // selected id that is not among the items yet has to be reported again once
  // the item is loaded. Remembering only the id of a *resolved* item made the
  // guard below permanently false for an id that never resolves, so every
  // settling fetch notified again - a loop when the handler updates state the
  // autocomplete renders from (LEXIAN-8417).
  const lastNotified = useRef<{id: string | undefined; hadItem: boolean}>(
    undefined
  );

  useEffect(() => {
    // a multi-select value reaches `useSelectedId` as `undefined`
    if (!onSelectedItemChange || multiple) {
      return;
    }
    if (!selectedId) {
      // Nothing reported yet means it mounted without a selection: the caller
      // needs that one report to know there is nothing to load.
      if (
        lastNotified.current === undefined ||
        lastNotified.current.id !== undefined
      ) {
        lastNotified.current = {id: undefined, hadItem: false};
        onSelectedItemChange(undefined);
      }
      return;
    }

    const selected = [...additionalItems, ...items].find(
      (item) => toOption(item).value === selectedId
    );
    const alreadyNotified =
      lastNotified.current?.id === selectedId &&
      // a previously unresolved id is reported again as soon as it resolves
      (lastNotified.current.hadItem || selected === undefined);
    if (alreadyNotified) {
      return;
    }
    lastNotified.current = {id: selectedId, hadItem: selected !== undefined};
    onSelectedItemChange(selected);
    // `toOption` is only used to look up the id of an item and is read from the
    // closure of the settling render - reacting to its identity would re-run
    // this effect on every render of a caller that passes an inline arrow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSelectedItemChange, selectedId, multiple, items, additionalItems]);
}

/**
 * Builds an `AutocompleteLoader` for a standard paginated search endpoint. The
 * live search query is always sent as `query`; `params` adds fixed and
 * filter-derived parameters (annotate its parameter to bind `TFilterState`).
 */
export function paginatedLoader<
  TItem,
  BODY,
  OTHER extends object,
  TFilterState = never,
>({
  endpointFunction,
  getPageItemFromResponse,
  params,
}: {
  endpointFunction: (
    args: HandledArgs<OTHER>
  ) => Promise<HandledResponseType<BODY>>;
  getPageItemFromResponse: (body: BODY) => PaginationResponse<PageItem<TItem>>;
  params?: (filterState: TFilterState | undefined) => Partial<OTHER>;
}): AutocompleteLoader<TItem, TFilterState> {
  return (paginationArguments, currentLoadable, {searchQuery, filterState}) =>
    handlePaginatedLoadableFetch<OTHER, BODY, TItem>(
      {
        endpointFunction,
        // The merge cannot be statically proven to equal OTHER, but
        // `Partial<OTHER>` on `params` keeps key-level checking.
        otherParams: {query: searchQuery, ...params?.(filterState)} as OTHER,
        getPageItemFromResponse,
      },
      paginationArguments,
      currentLoadable
    );
}

/**
 * Builds an `AutocompleteLoader` for a static item list (e.g. masterdata). It
 * deliberately ignores the search query - static wrappers keep the client-side
 * option filtering of the autocomplete.
 */
export function staticLoader<TItem, TFilterState = never>(
  getItems: (filterState: TFilterState | undefined) => TItem[]
): AutocompleteLoader<TItem, TFilterState> {
  return (_paginationArguments, _currentLoadable, {filterState}) =>
    Promise.resolve({
      state: State.Success,
      hasMoreBefore: false,
      hasMoreAfter: false,
      data: getItems(filterState).map((item, index) => ({
        cursor: String(index),
        item,
      })),
    });
}

export const AutocompleteFilterRow = styled.div`
  ${mixinFlexboxRow};
  gap: var(--gutter);
`;

export const AutocompleteFilterColumn = styled.div`
  ${mixinFlexboxColumn};
  gap: var(--gutter-small);
`;
